

































































































import BasicButton from '@/ui-components/BasicButton.vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { namespace } from 'vuex-class';

import { CollectionType } from '@/enums/collection-type';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { NewPageType } from '@/@types/graphql-types';
import { providerPageExpTrackEvent } from '@/helpers/experiments/popular-provider-page-exp-helper';
import { FilterCollection, FilterName } from '@/stores/filter.store';

const ContentHeaderPersonDetails = () =>
	import(
		/* webpackChunkName: "content-header-person" */ '@/components/content-header/ContentHeaderPersonDetails.vue'
	);

const Filter = namespace('filter');
const Routing = namespace('routing');
const User = namespace('user');

export enum Event {
	RESIZE = 'resize',
}
export interface ResizeEvent {
	oldSize: number;
	size: number;
}

@Component({
	name: 'ContentHeader',
	components: { BasicButton, ContentHeaderPersonDetails },
})
export default class ContentHeader extends Vue {
	@Prop({ default: null }) personId?: string | null;
	@Prop({ default: null }) personBackdrop?: string | null;
	@Prop({ type: Boolean, default: false }) isSearchView?: boolean;
	@Prop() showFilterButton?: boolean;
	@Prop() lockedCollectionType: CollectionType; // which collection type it should only show changes
	@Prop() isFiltersVisible: boolean;

	// POPULAR-PROVIDER-PAGE-EXP START
	@Prop({ default: true }) showTitle: boolean;
	@Prop({ default: true }) showSubTitle: boolean;
	@Prop({ default: true }) fullWidth: boolean;

	get hasTitleSlot() {
		return !!this.$slots['title'];
	}

	// POPULAR-PROVIDER-PAGE-EXP END

	expanded = false;
	isFilterContent = false;
	isMadeInEurope = false;
	lockedUrlMetadata: any = null;
	query = {} as Route['query'];
	tempFilters = {} as FilterCollection;

	@Filter.Action resetFilters: (payload: { filterNames: FilterName[] }) => void;
	@Filter.Action setFilterValue: (payload: { filterName: FilterName; filterValue: any }) => void;
	@Filter.Getter filters: (collectionType: CollectionType) => FilterCollection;

	@Routing.State activeRoute: Route;
	@Routing.State urlMetadata: any;

	@User.Getter isLoggedIn: () => boolean;

	@Watch('urlMetadata', { immediate: true })
	onUrlMetadataChange() {
		if (
			this.activeRoute?.meta?.collectionType === this.lockedCollectionType &&
			// there's a brief time when urlMetadata hasn't been fetched yet where we'd use the urlMetadata of the previous site, e.g. TitleDetail's.
			this.urlMetadata &&
			this.urlMetadata.object_type === 'page'
		) {
			this.lockedUrlMetadata = this.urlMetadata;

			this.emitResize();

			const isLeavingSoonOrComingSoon = [NewPageType.LeavingSoon, NewPageType.Upcoming].includes(
				this.$route.meta?.newPageType
			);

			const search = this.$route.fullPath.split('?')[1] ? `?${this.$route.fullPath.split('?')[1]}` : '';
			this.isFilterContent =
				!!search &&
				!isLeavingSoonOrComingSoon &&
				search.indexOf('content_type') === -1 &&
				search.indexOf('secret') === -1 &&
				search.indexOf('login_prompt') === -1 &&
				search.indexOf('exclude_genres') === -1 &&
				search.indexOf('genres') === -1 &&
				// exclude case if sorting is the only filter active
				!(search.indexOf('?sort_by=') === 0 && search.indexOf('&') === -1);
		}
	}

	@Watch('activeRoute', { immediate: true })
	onActiveRouteChange() {
		if (this.activeRoute?.meta?.collectionType === this.lockedCollectionType) {
			this.query = this.activeRoute.query;
			this.isMadeInEurope = this.activeRoute.name === 'app.titles.popular.european';
		}
	}

	private emitResize() {
		const oldSize = this.$el?.getBoundingClientRect().height;

		if (oldSize !== undefined) {
			this.$nextTick(() => {
				const size = this.$el.getBoundingClientRect().height;
				this.$emit(Event.RESIZE, { size, oldSize } as ResizeEvent);
			});
		}
	}

	get ASSETS_DIR() {
		return '/' + ASSETS_DIR;
	}

	get userLoggedIn(): boolean {
		return this.isLoggedIn();
	}

	get currentFilters() {
		return this.filters(this.lockedCollectionType);
	}

	get h1Title() {
		return this.lockedUrlMetadata && this.lockedUrlMetadata.heading_1 ? this.lockedUrlMetadata.heading_1 : '';
	}

	get h2Title() {
		return this.lockedUrlMetadata && this.lockedUrlMetadata.heading_2 ? this.lockedUrlMetadata.heading_2 : '';
	}

	get h1Text() {
		return this.lockedUrlMetadata &&
			this.lockedUrlMetadata.html_content &&
			this.lockedUrlMetadata.html_content.h1_text
			? this.lockedUrlMetadata.html_content.h1_text
			: '';
	}

	get h2Text() {
		return this.lockedUrlMetadata &&
			this.lockedUrlMetadata.html_content &&
			this.lockedUrlMetadata.html_content.h2_text
			? this.lockedUrlMetadata.html_content.h2_text
			: '';
	}

	onToggle() {
		// implemented to recalc filter bar position according to new header height
		this.$nextTick(() => this.$emit('toggle'));
	}

	toggleExpand() {
		this.expanded = !this.expanded;
		this.onToggle();
		TrackingHelper.trackEvent('content_header', {
			action: this.expanded ? 'expanded' : 'collapsed',
		});
		providerPageExpTrackEvent({
			action: 'click',
			label: this.expanded ? 'content_header_expanded' : 'content_header_collapsed',
		});
	}

	async toggleFilters() {
		const toggledValue = !this.isFiltersVisible;
		TrackingHelper.trackEvent('userinteraction', {
			action: 'filter_bar_toggle',
			value: Number(toggledValue),
		});

		if (!toggledValue) {
			this.tempFilters = { ...this.currentFilters };
			const filterNames: FilterName[] = Object.keys(this.currentFilters) as FilterName[];
			this.resetFilters({ filterNames });
		} else {
			for (const key in this.tempFilters) {
				this.setFilterValue({ filterName: key as FilterName, filterValue: (this.tempFilters as any)[key] });
			}
		}

		await this.$emit('update:isFiltersVisible', toggledValue);
	}
}
